import { useState } from 'react'
import { calculatePasswordStrength } from 'src/common/helpers'
import { FormikHelpers } from 'formik'
import { useAuth } from 'src/common/context'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { alertNotification } from 'src/ui'

interface Values {
  password: string
}

interface State {
  error?: string
  success?: string
  bar: {
    strengthColor: string
    value: number
  }
  preferredMfa?: string
}

export const initialValues: Values = {
  password: ''
}

export const validate = (values: Values) => {
  const errors: Partial<Values> = {}
  let bar = {
    strengthColor: 'disabled',
    value: 0
  }
  if (!values.password) {
    errors.password = 'Required'
  } else {
    const { error, bar: barState } = calculatePasswordStrength(values.password)
    if (error) {
      errors.password = error
    }
    bar = barState
  }
  return { errors, bar }
}

const ResetPassword = () => {
  const { resetPassword, completeNewPassword, tempUser, getPreferredMfa, isAuthBypassed } = useAuth()

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const username = searchParams.get('user_name')
  const confirmationCode = searchParams.get('confirmation_code')
  const isNewPasswordRequired = searchParams.get('newPasswordRequired') === 'true'

  // Invalid URL if: not newPasswordRequired AND missing username/confirmationCode
  const invalidURL = !isNewPasswordRequired && !(username && confirmationCode)

  const [state, setState] = useState<State>({
    error: undefined,
    success: undefined,
    bar: {
      strengthColor: 'disabled',
      value: 0
    },
    preferredMfa: undefined
  })

  const onSubmit = async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
    const { password } = values

    try {
      if (isNewPasswordRequired && tempUser) {
        // Handle NEW_PASSWORD_REQUIRED challenge
        await completeNewPassword(tempUser, password)

        // After password change, require MFA setup unless bypass is enabled
        if (!isAuthBypassed()) {
          const preferredMfa = await getPreferredMfa()
          if (preferredMfa === 'NOMFA') {
            // open the SetupMfa flow via state - caller component will render modal when preferredMfa === 'NOMFA'
            setState({ ...state, success: 'Your password has been reset successfully', preferredMfa })
          } else {
            // proceed to login again after password change to trigger MFA challenge
            navigate('/mfa', { replace: true })
            alertNotification(
              'Your password has been reset successfully. Please log in using the MFA challenge.',
              'success'
            )
          }
        } else {
          // Bypass MFA setup - navigate to home
          navigate('/', { replace: true })
          alertNotification('Your password has been reset successfully.', 'success')
        }
      } else if (username && confirmationCode) {
        // Handle forgot password flow
        await resetPassword(username as string, confirmationCode as string, password)
        navigate('/login')
        alertNotification('Your password has been reset successfully.', 'success')
      } else {
        throw new Error('Invalid password reset request')
      }
      setSubmitting(false)
    } catch (err) {
      setState({ ...state, error: (err as Error).message, success: undefined })
      setSubmitting(false)
    }
  }

  return { state, setState, onSubmit, invalidURL }
}

export default ResetPassword
